/*
 * RIDECLOUD FAQ SECTION
 * Style: Original RideCloud — white background, simple accordion list
 * Open state: orange left border, orange +/- icon
 * Colours: #2e004f question text, #5a3d70 answer text, #e6531e open accent
 */

import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "What's included in a RideCloud subscription?",
    a: "All RideCloud subscriptions include comprehensive insurance, tyres, vehicle licensing, routine maintenance, tracking and 24/7 roadside assistance. Fuel and toll fees are excluded. You get a single, predictable monthly payment with no hidden costs.",
  },
  {
    q: "How do I apply?",
    a: "Approval is based on KYC (Know Your Customer) verification and affordability assessment — not a 6-year credit lock-in. If you can demonstrate consistent income, we want to work with you.",
  },
  {
    q: "What is the minimum subscription term?",
    a: "RideCloud subscriptions are available with initial terms from 12–36 months, depending on the vehicle. After your initial term, you can renew, upgrade to a different vehicle, or return the car — giving you flexibility without the long-term commitment of traditional finance.",
  },
  {
    q: "Can I swap my vehicle during the subscription?",
    a: "Yes! After your initial term, you can upgrade or swap to a different vehicle in our catalogue. This is one of the key advantages of subscription over traditional ownership — your car can evolve with your life.",
  },
  {
    q: "How does doorstep delivery work?",
    a: "Where available, we can arrange delivery of your vehicle directly to your home or office. A once-off delivery fee applies. Alternatively, you can collect from a RideCloud partner dealership at no additional cost.",
  },
  {
    q: "What documents do I need to apply?",
    a: "You'll need a valid South African ID, a valid driver's licence, and proof of income (last 3 months' payslips or bank statements). The entire application process is completed online — no branch visits required.",
  },
  {
    q: "Are there mileage limits?",
    a: "Yes, subscriptions include a choice of 1,500 km, 2,000 km or 2,500 km per month depending on the package you choose. Excess kilometres are charged at a per-km rate as per your contract.",
  },
  {
    q: "What happens at the end of my subscription?",
    a: "At the end of your term, you have three options: renew your current subscription, upgrade to a newer or different vehicle, or simply return the car. There are no balloon payments or residual obligations.",
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" className="py-16 md:py-20" style={{ backgroundColor: "#ffffff" }}>
      <div className="container">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: "#2e004f" }}>
            Frequently Asked Questions
          </h2>
          <p className="text-base" style={{ color: "#5a3d70" }}>
            Everything you need to know about subscribing to a vehicle with RideCloud.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-0">
          {faqs.map((faq, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="transition-all"
                style={{
                  borderBottom: "1px solid rgba(46,0,79,0.1)",
                  borderLeft: isOpen ? "3px solid #e6531e" : "3px solid transparent",
                }}
              >
                <button
                  className="w-full flex items-center justify-between py-4 px-3 text-left transition-colors"
                  style={{ backgroundColor: isOpen ? "rgba(230,83,30,0.03)" : "transparent" }}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span
                    className="text-base font-medium pr-4"
                    style={{ color: isOpen ? "#e6531e" : "#2e004f" }}
                  >
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0" style={{ color: "#e6531e" }}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-4">
                    <p className="text-sm leading-relaxed" style={{ color: "#5a3d70" }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
